import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { UploadFileTypes } from "../../constants";
import { parseAmount } from "../../services/utils";
import { Button } from "../buttons/Buttons";
import ViewMediaModal from "../modals/ViewMediaModal";
import styles from "./Table.module.css";

function CreditsTable({ columns, data, title, handlePaymentAction }) {
  const [tableData, setTableData] = useState(data);
  const columnCount = columns.length;

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    if (key === "action") {
      const statusOrder =
        direction === "asc" ? ["approve", "reject", "pending", null] : ["reject", "approve", "pending", null];
      const sortedData = [...tableData].sort((a, b) => {
        const aIndex = statusOrder.indexOf(a.status);
        const bIndex = statusOrder.indexOf(b.status);
        return aIndex - bIndex;
      });
      setTableData(sortedData);
      return;
    } else if (key === "due_date") {
      const sortedData = [...tableData].sort((a, b) => {
        const aDate = new Date(a.due_date);
        const bDate = new Date(b.due_date);
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      });
      setTableData(sortedData);
      return;
    }

    const sortedData = [...tableData].sort((a, b) => {
      const parseValue = (value) =>
        typeof value === "string" && value.includes("₹") ? parseInt(value.replace(/₹|,/g, "")) : value;
      const aValue = parseValue(a[key]) ?? "";
      const bValue = parseValue(b[key]) ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setTableData(sortedData);
  };

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const paymentTypeText = {
    token: "Token",
    registrationcharges: "Registration Charges",
    developmentcharges: "Development Charges",
    downpayment: "Down Payment",
    emi: "EMI",
    booking_advance: "Booking Advance",
  };

  return (
    <>
      {title && <h2 className={styles.title}>{title}</h2>}
      <div className={`${styles.table} ${styles[`table_${columnCount}`]}`}>
        <div className={styles.table_header}>
          {columns.map((column, index) => (
            <div key={index} className={styles.table_header_cell}>
              <span className={styles.table_header_name}>{column.name}</span>
              {column.id !== "payment_proof" && (
                <button className={styles.sort_btn} onClick={() => handleSort(column.id)}>
                  <Icon
                    icon="ri:arrow-drop-down-line"
                    inline
                    className={`${styles.sort_icon} ${
                      sortConfig.key === column.id && sortConfig.direction === "asc" ? styles.asc : styles.desc
                    }`}
                  />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className={styles.table_body}>
          {tableData.length === 0 ? (
            <div className="row space-top small">No data added yet</div>
          ) : (
            tableData.map((row, index) => (
              <div key={index} className={styles.table_row_full}>
                {columns.map((column) => (
                  <div key={column.id} className={styles.table_body_cell}>
                    {column.id === "action" ? (
                      (row.status && row.status === "approve") || row.status === "reject" ? (
                        <span className="label">{row.status === "approve" ? "Approved" : "Rejected"}</span>
                      ) : (
                        <div className={styles.action_btn_group}>
                          <button
                            className={`${styles.action_btn} ${styles.approve}`}
                            onClick={() => handlePaymentAction(row.id, "approve", row.amount, row.payment_type)}
                          >
                            Approve
                          </button>
                          <button
                            className={`${styles.action_btn} ${styles.reject}`}
                            onClick={() => handlePaymentAction(row.id, "reject")}
                          >
                            Reject
                          </button>
                        </div>
                      )
                    ) : column.id === "payment_proof" ? (
                      row.payment_proof.id && row.payment_proof.url ? (
                        <Button
                          className="ghost"
                          onClick={() => {
                            setShowImageModal(true);
                            setImageUrl(row.payment_proof.url);
                          }}
                        >
                          View
                        </Button>
                      ) : (
                        <span className="small gray">No screenshot</span>
                      )
                    ) : column.id === "payment_type" ? (
                      paymentTypeText[row.payment_type] ?? row.payment_type
                    ) : column.id === "amount" ? (
                      parseAmount(row.amount)
                    ) : (
                      row[column.id] ?? "-"
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
      {showImageModal &&
        createPortal(
          <ViewMediaModal
            isVisible={showImageModal}
            handleClose={() => {
              setShowImageModal(false);
              setImageUrl("");
            }}
            mediaType={UploadFileTypes.IMAGES}
            url={[imageUrl]}
            title="Payment Proof"
          />,
          document.body
        )}
    </>
  );
}

export default CreditsTable;
